import { useEffect, useState } from "react";
import { Award, CalendarClock, Check, PiggyBank, Trash2, UserCheck, UserX, X, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { academyPermissions } from "@/lib/academyAccess";
import { money, fmtDate, fmtDateTime, editionLabel, selectCls, btnBrand, PRACTICE_STATUS, practiceStatus } from "@/lib/academyFormat";
import ReasonDialog from "@/components/academy/ReasonDialog";
import { ACADEMY } from "@/constants/testIds";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function MoveDialog({ enrollment: e, open, onOpenChange, onDone }) {
  const [editions, setEditions] = useState([]);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setError("");
    setReason("");
    setTarget(e.no_show_response?.edition_id || "");
    api.get("/academy/editions", { params: { course_id: e.course_id, upcoming: true } })
      .then(({ data }) => setEditions(data.filter((x) => x.id !== e.edition_id))).catch(() => setEditions([]));
  }, [open, e]);
  const save = async () => {
    setError("");
    try {
      await api.post(`/academy/enrollments/${e.id}/move`, { edition_id: target, reason: reason.trim() || null });
      onDone();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={ACADEMY.moveModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cambiar de fecha</DialogTitle>
          <DialogDescription>Pasa a otra fecha de {e.course_name} con todo lo pagado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha nueva</Label>
            <select className={selectCls} value={target} onChange={(ev) => setTarget(ev.target.value)} data-testid={ACADEMY.moveEditionSelect}>
              <option value="">Elegí una fecha</option>
              {editions.map((x) => <option key={x.id} value={x.id}>{editionLabel(x)}{x.location ? ` · ${x.location}` : ""}{x.capacity ? ` (${x.seats_taken} de ${x.capacity})` : ""}</option>)}
            </select>
            {editions.length === 0 && <p className="text-xs text-muted-foreground mt-1">No hay otras fechas próximas de este curso.</p>}
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Motivo (opcional)</Label>
            <Input value={reason} onChange={(ev) => setReason(ev.target.value)} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={!target} data-testid={ACADEMY.moveSaveButton}>Cambiar de fecha</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreditDialog({ enrollment: e, open, onOpenChange, onDone }) {
  const max = Math.min(e.student?.credit_balance || 0, e.account?.balance || 0);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setAmount(String(Math.round(max * 100) / 100)); setError(""); } }, [open, max]);
  const save = async () => {
    try {
      await api.post(`/academy/enrollments/${e.id}/apply-credit`, { amount: Number(amount) });
      onDone();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={ACADEMY.creditModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Usar saldo a favor</DialogTitle>
          <DialogDescription>Tiene {money(e.student?.credit_balance)} a favor. La inscripción debe {money(e.account?.balance)}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(ev) => setAmount(ev.target.value)} data-testid={ACADEMY.creditAmountInput} />
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} data-testid={ACADEMY.creditSaveButton}>Usar saldo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Fix Y (2026-10-13): lo que pasa despues de inscribirse: asistencia, practicas,
// inasistencia (24 h para responder), cambio de fecha, saldo a favor y diploma.
export default function EnrollmentProgress({ enrollment: e, onChanged }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const mine = academyPermissions(user);
  const isAdmin = user?.role === "admin";
  // Fix D (2026-10-18): recepcion tambien marca asistencia y practicas (carta, punto 17)
  const canTeach = isAdmin || mine.includes("academic") || mine.includes("instructor") || mine.includes("reception");
  const isAcademic = isAdmin || mine.includes("academic");
  const canToCredit = isAdmin || mine.includes("academic") || mine.includes("finance");
  const canUseCredit = isAdmin || mine.some((p) => ["finance", "reception", "academic"].includes(p));
  const [practice, setPractice] = useState({ date: todayIso(), time: "" });
  const [moveOpen, setMoveOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [toCreditOpen, setToCreditOpen] = useState(false);
  const acc = e.account || {};
  const st = e.status;
  const courseDay = (e.edition?.start_date || "9999") <= todayIso();
  const done = ["attended", "completed"].includes(st);
  const active = !["cancelled", "expired", "transferred"].includes(st);

  const call = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast({ title: ok });
      onChanged();
    } catch (err) {
      toast({ title: "No se pudo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };
  const attendance = (status, ok) => call(() => api.post(`/academy/enrollments/${e.id}/attendance`, { status }), ok);
  const withNote = (url) => async (note) => {
    try {
      await api.post(url, { note });
      onChanged();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid={`${ACADEMY.enrollmentProgressPrefix}${e.id}`}>
      {canTeach && ["pending", "confirmed"].includes(st) && courseDay && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2">
          <span className="text-sm text-muted-foreground mr-auto">Asistencia al curso</span>
          <Button size="sm" variant="outline" className="text-[#5B7A54]" onClick={() => attendance("attended", "Asistencia marcada")} data-testid={`${ACADEMY.attendedButtonPrefix}${e.id}`}><UserCheck className="w-4 h-4" /> Asistió</Button>
          <Button size="sm" variant="outline" className="text-[#A34B49]" onClick={() => attendance("no_show", "Marcado: no asistió")} data-testid={`${ACADEMY.noShowButtonPrefix}${e.id}`}><UserX className="w-4 h-4" /> No asistió</Button>
        </div>
      )}

      {st === "no_show" && (
        <div className="rounded-lg border border-[#E8C9C8] bg-[#FBF1F1] px-3 py-2 space-y-2" data-testid={`${ACADEMY.noShowBoxPrefix}${e.id}`}>
          <p className="text-sm text-[#A34B49]">No asistió. Tiene hasta el {fmtDateTime(e.no_show_deadline)} para responder desde su página; si no responde, la reserva vence sola.</p>
          {e.no_show_response ? (
            <p className="text-sm">
              <strong>Respondió:</strong> {e.no_show_response.action === "reschedule" ? `pide pasar al ${e.no_show_response.edition_text}` : "explicó lo que pasó"}
              {e.no_show_response.message ? ` — "${e.no_show_response.message}"` : ""}
            </p>
          ) : <p className="text-xs text-muted-foreground">Todavía no respondió.</p>}
          <div className="flex flex-wrap gap-2">
            {isAcademic && <Button size="sm" className={btnBrand} onClick={() => setMoveOpen(true)} data-testid={`${ACADEMY.moveButtonPrefix}${e.id}`}><CalendarClock className="w-4 h-4" /> Pasar a otra fecha</Button>}
            {isAcademic && <Button size="sm" variant="outline" onClick={() => setExpireOpen(true)} data-testid={`${ACADEMY.expireButtonPrefix}${e.id}`}><X className="w-4 h-4" /> Dar por vencida</Button>}
            {canTeach && <Button size="sm" variant="ghost" onClick={() => attendance("attended", "Asistencia corregida")}><UserCheck className="w-4 h-4" /> Sí asistió</Button>}
          </div>
        </div>
      )}

      {done && (
        <div className="rounded-lg border border-border px-3 py-2 space-y-2" data-testid={`${ACADEMY.practicesBoxPrefix}${e.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm">
              <span className="text-[#5B7A54] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Asistió</span>
              <span className="text-muted-foreground"> · Prácticas </span>
              <strong data-testid={`${ACADEMY.practicesCountPrefix}${e.id}`}>{e.diploma?.practices_done ?? 0} de {e.diploma?.practices_required ?? 0}</strong>
            </p>
            {canTeach && <Button size="sm" variant="ghost" onClick={() => attendance("confirmed", "Asistencia deshecha")} title="Deshacer la asistencia"><Undo2 className="w-4 h-4" /></Button>}
          </div>
          {(e.practices || []).length > 0 && (
            <div className="divide-y divide-border text-sm">
              {[...e.practices].sort((x, y) => `${x.date} ${x.time || ""}`.localeCompare(`${y.date} ${y.time || ""}`)).map((p) => {
                // Fix D (2026-10-18): en orden de fecha; la agendada se marca si asistio o falto
                const ps = practiceStatus(p);
                const info = PRACTICE_STATUS[ps];
                return (
                  <div key={p.id} className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`${ACADEMY.practiceRowPrefix}${p.id}`}>
                    <span className="min-w-[110px]">{fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""}</span>
                    <span className={`text-xs rounded px-2 py-0.5 ${info.cls}`} data-testid={`${ACADEMY.practiceStatusPrefix}${p.id}`}>{info.label}</span>
                    <span className="text-xs text-muted-foreground flex-1">{p.marked_by_name || p.by_name}{p.note ? ` · ${p.note}` : ""}</span>
                    {canTeach && ps === "scheduled" && p.date <= todayIso() && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-[#5B7A54]" onClick={() => call(() => api.put(`/academy/enrollments/${e.id}/practices/${p.id}`, { status: "attended" }), "Práctica marcada")} data-testid={`${ACADEMY.practiceAttendedPrefix}${p.id}`}><UserCheck className="w-3.5 h-3.5" /> Asistió</Button>
                        <Button size="sm" variant="outline" className="h-7 text-[#A34B49]" onClick={() => call(() => api.put(`/academy/enrollments/${e.id}/practices/${p.id}`, { status: "absent" }), "Marcado: faltó")} data-testid={`${ACADEMY.practiceAbsentPrefix}${p.id}`}><UserX className="w-3.5 h-3.5" /> Faltó</Button>
                      </>
                    )}
                    {canTeach && ps !== "scheduled" && p.status && <Button size="icon" variant="ghost" className="h-7 w-7" title="Deshacer la marca" onClick={() => call(() => api.put(`/academy/enrollments/${e.id}/practices/${p.id}`, { status: "scheduled" }))} data-testid={`${ACADEMY.practiceUndoPrefix}${p.id}`}><Undo2 className="w-3.5 h-3.5" /></Button>}
                    {canTeach && <Button size="icon" variant="ghost" className="h-7 w-7" title="Borrar" onClick={() => call(() => api.delete(`/academy/enrollments/${e.id}/practices/${p.id}`))}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>}
                  </div>
                );
              })}
            </div>
          )}
          {canTeach && (
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={practice.date} onChange={(ev) => setPractice((x) => ({ ...x, date: ev.target.value }))} className="w-40 h-8" data-testid={`${ACADEMY.practiceDateInputPrefix}${e.id}`} />
              <Input type="time" value={practice.time} onChange={(ev) => setPractice((x) => ({ ...x, time: ev.target.value }))} className="w-28 h-8" data-testid={`${ACADEMY.practiceTimeInputPrefix}${e.id}`} />
              {practice.date >= todayIso() && <Button size="sm" variant="outline" onClick={() => call(() => api.post(`/academy/enrollments/${e.id}/practices`, { date: practice.date, time: practice.time || null, status: "scheduled" }), "Práctica agendada")} data-testid={`${ACADEMY.practiceScheduleButtonPrefix}${e.id}`}><CalendarClock className="w-4 h-4" /> Agendar práctica</Button>}
              {practice.date <= todayIso() && <Button size="sm" variant="ghost" onClick={() => call(() => api.post(`/academy/enrollments/${e.id}/practices`, { date: practice.date, time: practice.time || null, status: "attended" }), "Práctica anotada")} data-testid={`${ACADEMY.practiceAddButtonPrefix}${e.id}`}><Check className="w-4 h-4" /> Ya la hizo</Button>}
            </div>
          )}
          {e.diploma?.ready ? (
            <a href={`${API}/academy/enrollments/${e.id}/diploma`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#8A6A2F]" data-testid={`${ACADEMY.diplomaLinkPrefix}${e.id}`}><Award className="w-4 h-4" /> Ver diploma{e.diploma_number ? ` N° ${e.diploma_number}` : ""}</a>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid={`${ACADEMY.diplomaMissingPrefix}${e.id}`}>Para el diploma falta: {(e.diploma?.missing || []).join(" · ")}</p>
          )}
        </div>
      )}

      {st === "transferred" && <p className="text-sm text-muted-foreground">Lo pagado ({money(e.credit_amount)}) pasó a saldo a favor del alumno{e.credit_note ? `: ${e.credit_note}` : ""}.</p>}
      {(e.moves || []).length > 0 && <p className="text-xs text-muted-foreground">Cambió de fecha {e.moves.length === 1 ? "una vez" : `${e.moves.length} veces`} (última: {fmtDateTime(e.moves[e.moves.length - 1].at)}{e.moves[e.moves.length - 1].reason ? `, ${e.moves[e.moves.length - 1].reason}` : ""}).</p>}

      <div className="flex flex-wrap gap-2">
        {isAcademic && ["pending", "confirmed"].includes(st) && <Button size="sm" variant="outline" onClick={() => setMoveOpen(true)} data-testid={`${ACADEMY.changeDateButtonPrefix}${e.id}`}><CalendarClock className="w-4 h-4" /> Cambiar de fecha</Button>}
        {canUseCredit && active && (e.student?.credit_balance || 0) > 0 && acc.balance > 0 && <Button size="sm" variant="outline" onClick={() => setCreditOpen(true)} data-testid={`${ACADEMY.useCreditButtonPrefix}${e.id}`}><PiggyBank className="w-4 h-4" /> Usar saldo a favor ({money(e.student.credit_balance)})</Button>}
        {canToCredit && !["transferred", "attended", "completed"].includes(st) && acc.net_paid > 0 && <Button size="sm" variant="ghost" onClick={() => setToCreditOpen(true)} data-testid={`${ACADEMY.toCreditButtonPrefix}${e.id}`}><PiggyBank className="w-4 h-4" /> Pasar a saldo a favor</Button>}
      </div>

      <MoveDialog enrollment={e} open={moveOpen} onOpenChange={setMoveOpen} onDone={() => { toast({ title: "Fecha cambiada" }); onChanged(); }} />
      <CreditDialog enrollment={e} open={creditOpen} onOpenChange={setCreditOpen} onDone={() => { toast({ title: "Saldo a favor usado" }); onChanged(); }} />
      <ReasonDialog open={expireOpen} onOpenChange={setExpireOpen} title="Dar por vencida" description="La reserva vence y la inscripción deja de estar activa."
        confirmLabel="Dar por vencida" onConfirm={withNote(`/academy/enrollments/${e.id}/expire`)} />
      <ReasonDialog open={toCreditOpen} onOpenChange={setToCreditOpen} title="Pasar a saldo a favor"
        description={`Lo pagado (${money(acc.net_paid)}) queda a favor del alumno para otro curso y esta inscripción deja de estar activa.`}
        confirmLabel="Pasar a saldo a favor" onConfirm={withNote(`/academy/enrollments/${e.id}/to-credit`)} />
    </div>
  );
}
